/**
 * LoginTermsNotice — the terms/privacy notice shown under the login provider
 * buttons.
 *
 * Flutter source of truth: `LoginScreen.termsSections()`
 * (`lib/presentation/screens/login/login_screen.dart:444-495`):
 *   - A centred `RichText` (`:447-448`) inside
 *     `Padding(left: 16, right: 16, bottom: 8)` (`:446`).
 *   - The prefix span (`:450-455`): "By continuing, you affirm that you are the
 *     age of majority in your jurisdiction and you agree to the ", styled
 *     `AppColors.disabledTextColor` / `fontSize: 14`. The web previously said
 *     only "By continuing, you agree to our …" — the **age-of-majority
 *     affirmation is restored here** (it now lives in
 *     `l10n.auth.termsNoticePrefix`, which owns its trailing space).
 *   - The first link span (`:457-470`) taps to
 *     `NavigationService.navigateToTermsScreen()` (`:468`); the `' and '`
 *     separator span (`:471-477`) is `l10n.auth.termsNoticeSeparator` (which
 *     owns its leading + trailing spaces); the second link span (`:478-490`)
 *     taps to `NavigationService.navigateToPrivacyPolicyScreen()` (`:488`).
 *     Both destinations map 1:1 onto `Navigator.navigateToTermsOfUse()` /
 *     `Navigator.navigateToPrivacyPolicy()` — in-app SPA navigation, not the
 *     raw `<a href>` anchors this component replaced (those forced a full page
 *     reload). Both legal routes are `PUBLIC_ROUTES`, so they stay reachable
 *     from the unauthenticated login page.
 *
 * Intentional web deviations (UI idiom + document naming only — the controls
 * and their destinations match Flutter exactly):
 *
 *   1. **Document naming.** Flutter's login label is `'Terms of Service'`
 *      (`:458`); the web uses its own `l10n.legal.termsOfUse` / `.privacyPolicy`
 *      names, the same choice `LegalDocumentUpdatedModal` already ships and the
 *      title the web's own `/legal/terms-of-use` page carries. This is more
 *      than a preference: Flutter names the *same* document three ways —
 *      `login_screen.dart:458` "Terms of Service",
 *      `legal_documents_screen.dart:24` "Terms of Use", and the URL it actually
 *      opens is `https://expause.com/terms-of-use/` under the webview title
 *      "Terms of Service" (`navigation_service.dart:1528-1529`). The published
 *      document is *Terms of Use*, so the web label matches reality and
 *      Flutter's login label does not. Normalising Flutter's label across its
 *      call sites is a multi-site Dart copy change on a live app and is
 *      explicitly out of scope for this branch.
 *      Bug found on Flutter, web implemented correctly; follow-up recommended to fix Flutter bug.
 *
 *   2. **Link styling.** Flutter paints the links `AppColors.white` +
 *      `FontWeight.w600`; the web paints them `theme.blue` +
 *      `responsive.fontWeight.semibold` — the web's link role, matching this
 *      screen's other blue links and the sibling legal sentence in
 *      `PremiumPackagesPage.tsx:145-174`. They render as the shared
 *      `ExpauseButton` `ButtonVariant.text`, which `ExpauseButton.tsx:12-19`
 *      documents as *the* inline-in-a-paragraph control (the
 *      `ContactSupportSection.tsx:49-60` precedent).
 *
 *   3. **Font size — consciously confirmed, keep `responsive.font.sm`.**
 *      Flutter renders the whole notice at a flat `fontSize: 14`
 *      (`:454`/`:461`/`:475`/`:482`), while `responsive.font.sm` is 12px on
 *      mobile / 14px on desktop (`responsiveDesign.ts` →
 *      `MOBILE_RESPONSIVE.font.sm` / `DESKTOP_RESPONSIVE.font.sm`) — so the
 *      mobile notice is 2px smaller than Flutter's. That is accepted on
 *      purpose: `conventions.md` permits responsive sizing to deviate,
 *      Flutter-14 → `responsive.font.sm` is the project's established mapping
 *      (`MoreNotificationsRow.tsx:35` documents it for the same 14px style),
 *      and the web's sibling legal sentence — the `PremiumPackagesPage` policy
 *      text (`:147`) — already renders at `font.sm`; matching it keeps the two
 *      legal notices visually consistent, which matters more here than a 2px
 *      mobile match. Do NOT substitute `font.base` to chase Flutter's number.
 *
 * This component paints NO outer margin — the section spacing between the
 * buttons and the notice is owned by `LoginPage`.
 */
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseButton, ButtonVariant } from '@/presentation/components/common/controls/ExpauseButton';
import { Navigator } from '@/presentation/routing/Navigator';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const LoginTermsNotice = () => {
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const linkStyle = {
    fontSize: responsive.font.sm,
    fontWeight: responsive.fontWeight.semibold,
    fontFamily: 'inherit',
  };

  return (
    <p
      style={{
        margin: 0,
        textAlign: 'center',
        fontSize: responsive.font.sm,
        color: theme.text.secondary,
        lineHeight: 1.5,
      }}
    >
      {/* The prefix and separator carry their own leading/trailing spaces (they
          are pinned that way in `l10n.auth.*`), so no literal spaces or `{' '}`
          are added around the links here — doing so would double-space them. */}
      {l10n.auth.termsNoticePrefix}
      <ExpauseButton
        {...qaAttr({ id: 'login-terms' })}
        variant={ButtonVariant.text}
        textColor={theme.blue}
        onClick={() => Navigator.navigateToTermsOfUse()}
        style={linkStyle}
      >
        {l10n.legal.termsOfUse}
      </ExpauseButton>
      {l10n.auth.termsNoticeSeparator}
      <ExpauseButton
        {...qaAttr({ id: 'login-privacy' })}
        variant={ButtonVariant.text}
        textColor={theme.blue}
        onClick={() => Navigator.navigateToPrivacyPolicy()}
        style={linkStyle}
      >
        {l10n.legal.privacyPolicy}
      </ExpauseButton>
    </p>
  );
};
